import { client } from "@/sanity/lib/client";
import Link from "next/link";
import Image from "next/image";
import MetaTags from "@/components/MetaTags";
import { useRouter } from "next/router";
import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import imageUrlBuilder from "@sanity/image-url";
import { IoEllipsisHorizontal } from "react-icons/io5";
import Sidebar from "@/components/learn/Sidebar";

dayjs.extend(relativeTime);

const builder = imageUrlBuilder(client);

function urlFor(source) {
  return builder.image(source);
}

export default function Learn({
  posts,
  categories,
  postCount,
  categoriesWithPostCounts,
}) {
  const [displayedPosts, setDisplayedPosts] = useState(posts);
  const [offset, setOffset] = useState(posts.length);
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const loadMorePosts = async () => {
    setLoading(true);
    const morePosts = await fetch(`/api/load-more-posts?offset=${offset}`).then(
      (res) => res.json(),
    );
    setLoading(false);
    setDisplayedPosts((prevPosts) => [...prevPosts, ...morePosts]);
    setOffset((prevOffset) => prevOffset + morePosts.length);
  };

  useEffect(() => {
    setDisplayedPosts(posts);
  }, [posts]);

  return (
    <>
      <MetaTags
        pageTitle="Learn"
        pageDescription="Learn about the latest trends in eCommerce, web design, and digital marketing."
        path="/learn"
      />
      <div>
        {/* <Hero
          title="Learn Web Design and Marketing"
          subtitle="Resources"
          description="Stay up to date with the latest trends in eCommerce, web design, and digital marketing."
        /> */}
        <div className="wrapper mb-20 mt-10 flex flex-col gap-5 lg:flex-row">
          <ul className="mx-auto grid w-full gap-12 lg:w-4/5 lg:max-w-5xl xl:max-w-none">
            {displayedPosts.map((post) => {
              return (
                <li
                  key={post._id}
                  className="mx-auto w-full max-w-xl lg:max-w-none"
                >
                  <div className="flex w-full flex-col gap-5 lg:flex-row lg:gap-8">
                    <div className="relative mr-auto shrink-0 sm:h-[114px] sm:w-[342px]">
                      {post.mainImage ? (
                        <Link href={`/learn/${post.slug.current}`}>
                          <Image
                            src={urlFor(post.mainImage).url()}
                            width={600}
                            height={400}
                            alt={post.title}
                            className="h-full w-full object-cover"
                          />
                        </Link>
                      ) : (
                        <div className="h-[114px] w-[342px] bg-neutral-100"></div>
                      )}
                    </div>
                    <div className="w-full">
                      <div className="mb-2.5 flex flex-wrap items-center gap-2.5">
                        <div className="flex flex-wrap gap-1.5">
                          {post.categories
                            .map((category) => {
                              return (
                                <Link
                                  href={`/learn/category/${category.slug.current}`}
                                  key={category._id}
                                  className="rounded-full bg-neutral-100 px-2 py-1 text-xs leading-none text-neutral-700 hover:!underline"
                                >
                                  {category.title}
                                </Link>
                              );
                            })
                            .slice(0, 2)}
                        </div>
                        <p className="text-xs text-neutral-500">
                          {dayjs(post.publishedAt).format("MMMM D, YYYY")}
                        </p>
                      </div>
                      <Link href={`/learn/${post.slug.current}`}>
                        <h2 className="mb-2 text-xl font-bold underline">
                          {post.title}
                        </h2>
                      </Link>
                      <p className="line-clamp-2 text-neutral-500">
                        {post.excerpt}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
            {displayedPosts.length < postCount && (
              <button
                className="rounded-md bg-gray-100 px-5 py-3 text-sm font-medium text-neutral-800 active:bg-gray-200"
                onClick={loadMorePosts}
              >
                {loading ? (
                  <div className="flex w-full items-center justify-center gap-2.5">
                    Loading posts...{" "}
                    <IoEllipsisHorizontal className="mr-2 animate-spin" />
                  </div>
                ) : (
                  "Load more"
                )}
              </button>
            )}
          </ul>
          <Sidebar
            categories={categories}
            categoriesWithPostCounts={categoriesWithPostCounts}
          />
        </div>
      </div>
    </>
  );
}

export async function getStaticProps() {
  const postCount = await client.fetch(`count(*[_type == "post"])`);

  const posts = await client.fetch(
    `*[_type == "post"] | order(publishedAt desc)[0...7] {
      _id,
      title,
      publishedAt,
      slug,
      mainImage,
      "categories": categories[]->{
        _id,
        title,
        slug
      },
      body,
      excerpt,
      "author": author->name
    }`,
  );

  const categories = await client.fetch(
    `*[_type == "category"]{
      _id,
      title,
      slug
    }`,
  );

  const categoriesWithPostCounts = await Promise.all(
    categories.map(async (category) => {
      const postCountQuery = `count(*[_type == "post" && references($categoryId)])`;
      const postCount = await client.fetch(postCountQuery, {
        categoryId: category._id,
      });
      return {
        ...category,
        postCount,
      };
    }),
  );

  if (!posts) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      posts,
      categories,
      postCount,
      categoriesWithPostCounts,
    },
    revalidate: 60,
  };
}
